import React from 'react';
import PropTypes from 'prop-types';
import {
  CAN_APPROVE_LEAD_VAULT_ACCESS_TYPES,
  CAN_REVOKE_LEAD_VAULT_ACCESS_TYPES,
  CAN_UNBLOCK_LEAD_VAULT_ACCESS_TYPES
} from '../../reducers/leads';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MenuItem, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { faUserCog } from '@fortawesome/pro-solid-svg-icons';

const AccessOptions = ({
  iconClassName,
  lead,
  currentUser,
  approveLeadVaultAccess,
  refreshLead,
  revokeLeadVaultAccess,
  blockLeadVaultAccess,
  unblockLeadVaultAccess,
}) => {
  const accessOptions = [];

  (lead.propertyVaultProfiles || []).forEach((propertyVaultProfile) => {
    const { id, inWaitlist, approvalState, approveAccessPath, revokeAccessPath, saleOrLease } = propertyVaultProfile;

    if (inWaitlist && CAN_APPROVE_LEAD_VAULT_ACCESS_TYPES.includes(approvalState)) {
      accessOptions.push(
        <MenuItem
          key={`approve-${id}`}
          name={`approveVaultAccess${id}`}
          onClick={() => {
            approveLeadVaultAccess(lead.id, id, approveAccessPath);
            document.body.click();
            refreshLead(lead);
          }}
        >
          <FontAwesomeIcon className={iconClassName} fixedWidth icon={faUserCog} size="sm"/>
          Approve {saleOrLease} Access
        </MenuItem>
      );
    }

    if (inWaitlist && CAN_REVOKE_LEAD_VAULT_ACCESS_TYPES.includes(approvalState)) {
      accessOptions.push(
        <MenuItem
          key={`revoke-${id}`}
          name={`revokeVaultAccess${id}`}
          onClick={() => {
            revokeLeadVaultAccess(lead.id, id, revokeAccessPath);
            document.body.click();
            refreshLead(lead);
          }}
        >
          <FontAwesomeIcon className={iconClassName}
            fixedWidth icon={faUserCog} size="sm"
          /> Revoke {saleOrLease} Access
        </MenuItem>
      );
    }
  });

  const profileId = lead.profileId;

  if (currentUser.canManageCompany && !CAN_UNBLOCK_LEAD_VAULT_ACCESS_TYPES.includes(lead.approvalStatus)) {
    const blockIconClassName = profileId ? iconClassName : iconClassName.replace('bo-menu-icon', 'text-muted');

    const menuItem = (
      <MenuItem
        disabled={!profileId}
        name="blockVaultAccess"
        onSelect={() => {
          blockLeadVaultAccess(lead.id, lead.projectId);
          document.body.click();
          refreshLead(lead);
        }}
      >
        <FontAwesomeIcon className={blockIconClassName} fixedWidth icon={faUserCog} size="sm"/> Block Access
      </MenuItem>
    );

    if (profileId) {
      accessOptions.push(<React.Fragment key={`block-${profileId}`}>{menuItem}</React.Fragment>);
    } else {
      const tooltip = (
        <Tooltip id={`tooltip-block-${profileId}`}>
          this lead doesn't have a buildout profile so they're unable to be blocked
        </Tooltip>
      );

      accessOptions.push(<OverlayTrigger key={`block-${profileId}`} overlay={tooltip} placement="left">
        {menuItem}
      </OverlayTrigger>);
    }
  } else if (currentUser.canManageCompany && CAN_UNBLOCK_LEAD_VAULT_ACCESS_TYPES.includes(lead.approvalStatus)) {
    accessOptions.push(
      <MenuItem
        key="unblock"
        name="unblockVaultAccess"
        onClick={() => {
          unblockLeadVaultAccess(lead.id, lead.projectId);
          document.body.click();
          refreshLead(lead);
        }}
      >
        <FontAwesomeIcon className={iconClassName} fixedWidth icon={faUserCog} size="sm"/> Unblock Access
      </MenuItem>
    );
  }

  return <React.Fragment>{accessOptions}</React.Fragment>;
};

AccessOptions.defaultProps = {
  iconClassName: 'mr-2'
};

AccessOptions.propTypes = {
  approveLeadVaultAccess: PropTypes.func.isRequired,
  blockLeadVaultAccess: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({
    canManageCompany: PropTypes.bool.isRequired
  }).isRequired,
  iconClassName: PropTypes.string,
  lead: PropTypes.object.isRequired,
  refreshLead: PropTypes.func.isRequired,
  revokeLeadVaultAccess: PropTypes.func.isRequired,
  unblockLeadVaultAccess: PropTypes.func.isRequired
};

export default AccessOptions;
